import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, ArrowRight, Send, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import SEO from '../components/SEO/SEO';
import { jobs } from '../data/jobs';

const JobListing = ({ id, title, department, location, type, salary, description }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl p-6 md:p-8 flex flex-col h-full justify-between gap-6">
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
            {type}
          </span>
          {department && (
            <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
              {department}
            </span>
          )}
          {salary && (
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
              {salary}
            </span>
          )}
          <div className="flex items-center text-slate-500 text-sm ml-2">
            <MapPin className="w-4 h-4 mr-1" />
            {location}
          </div>
        </div>
        <h3 className="text-2xl font-bold text-slate-900">{title}</h3>
        {description && (
          <p className="text-slate-500 mt-2 line-clamp-2 leading-relaxed">
            {description}
          </p>
        )}
      </div>
      
      <div className="pt-4 border-t border-slate-100 mt-auto">
        <Link 
          to={`/careers/${id}`} 
          className="bg-blue-600 text-white px-6 py-2 rounded-full font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 group w-full"
        >
          View Details
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

function Careers() {
  const [searchQuery, setSearchQuery] = useState('');

  // Schema for job postings
  const jobSchema = jobs.map(job => ({
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": "2024-05-01", // Should ideally be dynamic
    "validThrough": "2026-12-31",
    "employmentType": "FULL_TIME",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Cirronyx",
      "sameAs": "https://cirronyx.com"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location.includes("New York") ? "New York" : "Remote",
        "addressRegion": job.location.includes("New York") ? "NY" : "Global",
        "addressCountry": "US"
      }
    }
  }));

  return (
    <div className="bg-slate-50 min-h-screen">
      <SEO 
        title="Careers & Internships | Join Cirronyx in Cameroon"
        description="Join Cirronyx and help build the future of enterprise technology. Explore open positions and internship opportunities in Software Engineering, AI, Design, and Cyber Security in Yaoundé, Cameroon."
        keywords="careers at Cirronyx, tech jobs Cameroon, internships in Yaounde, IT jobs Yaounde, software engineering internship Yaounde, tech company hiring Yaounde, AI jobs Cameroon"
        schemaData={jobSchema}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-slate-900 to-blue-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="w-full h-full border-l border-b border-blue-400 rotate-12 transform origin-top-right"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-block mb-4">
            <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-4 py-1 rounded-full text-sm font-medium">
              Join Our Mission
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
            Build the Future of <span className="text-blue-400">Enterprise Tech</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
            We're a team of passionate architects, engineers, and innovators dedicated to solving 
            the world's most complex technology challenges. Join us and make an impact.
          </p>
          <div className="flex justify-center gap-4">
            <a href="#openings" className="bg-blue-600 text-white px-8 py-3 rounded-full font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-900/20">
              View Open Positions
            </a>
          </div>
        </div>
      </section>

      {/* Openings Section */}
      <section id="openings" className="py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Current Openings</h2>
          <p className="text-xl text-slate-600">Find your next challenge at Cirronyx</p>
        </div>

        <div className="max-w-xl mx-auto mb-12 relative">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-slate-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            type="text"
            className="w-full bg-white border border-slate-200 text-slate-900 rounded-full py-4 pl-12 pr-6 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            placeholder="Search by title, department, or keyword..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {jobs
            .filter(job => 
              job.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
              job.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              job.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              job.type.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((job, idx) => (
            <JobListing key={idx} {...job} />
          ))}
          {/*
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
              <Briefcase className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">No open positions at the moment</h3>
            <p className="text-slate-600 max-w-md mx-auto">
              We're not actively hiring for specific roles right now, but we are always looking for great talent. 
              Please feel free to submit a general application below!
            </p>
          </div>
          */}
        </div>
        
        <div className="mt-16 bg-blue-900 rounded-3xl p-10 text-white text-center shadow-2xl relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-3xl font-bold mb-4">Don't see a perfect fit?</h3>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              We're always looking for exceptional talent. Send us your resume and we'll keep 
              you in mind for future opportunities.
            </p>
            <a href="mailto:careers@cirronyx.com" className="bg-white text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-slate-100 transition inline-block">
              Send General Application
            </a>
          </div>
          {/* Decorative element */}
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-blue-600">
                <Briefcase className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Impactful Work</h3>
              <p className="text-slate-600">Work on mission-critical projects for Fortune 500 companies that shape industries.</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-cyan-600">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Flexibility</h3>
              <p className="text-slate-600">Remote-first culture with flexible working hours to support your work-life harmony.</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-purple-600">
                <Send className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Growth</h3>
              <p className="text-slate-600">Continuous learning budget, certification support, and clear paths for advancement.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Careers;
